// walkthrough/page-anchors.js — K855: what is actually on this page, right now.
//
// WHY THIS EXISTS
//   A generated walkthrough must never invent a control. The only way to be
//   sure a step points at something real is to read the live DOM after the
//   page has rendered, and to let nothing into a step that is not in this
//   list. The model that orders the steps chooses FROM this list; it cannot
//   add to it. Same contract as the route mode: the model selects, the client
//   validates, an unknown id is discarded rather than followed.
//
// AND WHY THE NAMES COME FROM HERE TOO
//   The step text is built from the element's own accessible name, never
//   written by the model. The adapter is textContent-only so markup is not
//   the risk; being WRONG is. "Click Delete to save" rendered next to a real
//   Delete button is worse than anything a support page could do, and the one
//   product whose pitch is verified claims cannot ship a confident invented
//   instruction. The model decides which controls matter and in what order;
//   every word on the card traces to the DOM or to a template here.
//
// WHAT IS DELIBERATELY EXCLUDED
//   * anything inside the walkthrough's own chrome, or it points at itself
//   * anything inside the assistant panel, same reason
//   * invisible elements - present is not visible, and a 0x0 target makes the
//     highlighter bail before it places the card (learned the hard way)
//   * elements with no usable name, because the step would read "Click ."

import { cssEscape } from './resolve.js';

const SKIP_CLOSEST = [
  '#g360-wt-card', '#g360-wt-ring', '#g360-wt-hand',
  '[data-testid="guide-outcome"]', '[data-g360-assistant]',
  /* G360_K879 - the application shell is not the page.

     AppShell renders <aside class="ap-side"> at line 251 and
     <section class="ap-page">{children}</section> at line 480, so the ENTIRE
     sidebar precedes the page in the DOM. Every nav item carries
     data-testid={'nav-' + item.id} (AppShell line 367) and there are 56
     surfaces, so a document-order walk with cap=40 filled the catalogue with
     40 navigation links and never reached a single control on the page the
     person was actually on.

     The model was then asked to order those into a walkthrough and correctly
     answered NONE - you cannot do a task with a list of menu links. That is
     why the guide never walked, on any page, and why a live check returned
     exactly 40 anchors: the cap, saturated by the sidebar. */
  '.ap-side', '.ap-top',
];

/* Interactive things a step can sensibly ask someone to use. A div with a
   testid is usually a container: pointing at it says "look here", which is
   fine for a destination but not for a step that says "click". */
const KIND_FOR = (el) => {
  const tag = el.tagName.toLowerCase();
  const role = (el.getAttribute('role') || '').toLowerCase();
  if (role === 'tab' || /(^|-)tab(-|$)/.test(el.getAttribute('data-testid') || '')) return 'tab';
  if (tag === 'button' || role === 'button') return 'button';
  if (tag === 'a') return 'link';
  if (tag === 'select' || role === 'combobox' || role === 'listbox') return 'select';
  if (tag === 'textarea') return 'input';
  if (tag === 'input') {
    const t = (el.getAttribute('type') || 'text').toLowerCase();
    if (t === 'checkbox' || t === 'radio') return 'choice';
    if (t === 'submit' || t === 'button') return 'button';
    return 'input';
  }
  return null;
};

/* Accessible-name-ish, in the order a screen reader would roughly resolve it.
   Capped because a step reading out an entire table cell helps nobody. */
function nameOf(el) {
  const pick = (v) => (v && String(v).trim() ? String(v).trim().replace(/\s+/g, ' ') : '');
  let n = pick(el.getAttribute('aria-label'));
  if (!n) {
    const by = el.getAttribute('aria-labelledby');
    if (by) {
      const parts = by.split(/\s+/).map((id) => document.getElementById(id)).filter(Boolean);
      n = pick(parts.map((p) => p.textContent).join(' '));
    }
  }
  if (!n) n = pick(el.getAttribute('title'));
  if (!n) n = pick(el.textContent);
  if (!n) n = pick(el.getAttribute('placeholder'));
  if (!n && el.id) {
    const lab = document.querySelector('label[for="' + cssEscape(el.id) + '"]');
    n = pick(lab && lab.textContent);
  }
  return n.length > 60 ? n.slice(0, 57) + '...' : n;
}

/* A MINIMUM SIZE, not a zero test. Two bugs in the first version: it
   required BOTH dimensions to be zero, so something 100 wide and 0 tall
   counted as visible; and a button styled width:0;height:0 still measures
   about 6x6 because of its default padding, so it passed anyway. A control
   that small cannot be clicked and cannot be pointed at - the hand would sit
   beside a speck - so it is not a step, whatever the box model says. 8px is
   below any real control and above every collapsed one. */
const MIN_PX = 8;
function visible(el) {
  const r = el.getBoundingClientRect();
  if (r.width < MIN_PX || r.height < MIN_PX) return false;
  const cs = window.getComputedStyle(el);
  return cs.visibility !== 'hidden' && cs.display !== 'none' && cs.opacity !== '0';
}

/**
 * Every usable, visible, named, interactive anchor on the page.
 * @param {number} cap keep the catalogue small - it becomes a prompt.
 * @returns {{testid:string,name:string,kind:string}[]}
 */
export function collectAnchors(cap = 40) {
  const out = [];
  const seen = new Set();
  let nodes;
  /* Scope to the page region when the shell provides one. SKIP_CLOSEST above
     would also drop the sidebar, but scoping is the stronger statement: the
     cap then applies to the page's own controls rather than being spent
     before the walk gets there. Falls back to the document so this keeps
     working anywhere the shell is absent. */
  let root = document;
  try { root = document.querySelector('.ap-page') || document; } catch { root = document; }
  try { nodes = root.querySelectorAll('[data-testid]'); } catch { return out; }
  for (const el of nodes) {
    const testid = el.getAttribute('data-testid');
    if (!testid || seen.has(testid)) continue;
    /* A duplicated testid is AMBIGUOUS and must not become a step: resolve.js
       treats ambiguity as a stop for exactly this reason, and a step pointing
       at whichever one came first is a guess wearing a instruction's clothes. */
    /* Deliberately document-wide even though collection is page-scoped:
       SurfaceGuide.find() and waitFor() resolve against the whole document,
       so a testid that is unique within .ap-page but repeated in the shell is
       still ambiguous at the moment it matters. Narrowing this to the page
       would hand back a step that cannot be resolved. */
    if (document.querySelectorAll('[data-testid="' + cssEscape(testid) + '"]').length > 1) {
      seen.add(testid);
      continue;
    }
    if (SKIP_CLOSEST.some((sel) => { try { return el.closest(sel); } catch { return false; } })) continue;
    const kind = KIND_FOR(el);
    if (!kind) continue;
    if (!visible(el)) continue;
    const name = nameOf(el);
    if (!name) continue;
    seen.add(testid);
    out.push({ testid, name, kind });
    if (out.length >= cap) break;
  }
  return out;
}

/** The catalogue as the model sees it. */
export function anchorCatalogue(anchors) {
  return anchors.map((a) => a.testid + ' | ' + a.kind + ' | ' + a.name).join('\n');
}

/* The sentence for a step, built from the control - never from the model.
   One template per kind, so the instruction can only ever be as wrong as the
   element's own label. */
export function instructionFor(a) {
  const q = '"' + a.name + '"';
  switch (a.kind) {
    case 'tab':    return 'Open the ' + q + ' tab, highlighted here.';
    case 'button': return 'Click ' + q + ', highlighted here.';
    case 'link':   return 'Follow ' + q + ', highlighted here.';
    case 'select': return 'Choose an option in ' + q + ', highlighted here.';
    case 'input':  return 'Enter your value in ' + q + ', highlighted here.';
    case 'choice': return 'Set ' + q + ', highlighted here.';
    default:       return 'This is ' + q + '.';
  }
}

/** Keep only steps whose testid is genuinely on this page, in model order. */
export function validateSteps(ids, anchors) {
  const by = new Map(anchors.map((a) => [a.testid, a]));
  const out = [];
  const used = new Set();
  for (const raw of ids || []) {
    const id = String(raw || '').trim();
    if (!id || used.has(id)) continue;      // no repeats: a loop is not a guide
    const a = by.get(id);
    if (!a) continue;                        // invented or stale - discarded
    used.add(id);
    out.push(a);
  }
  return out;
}

/* ---------------------------------------------------------------------------
   K857 - the model writes the instruction, and the page is checked afterwards.

   Templating every sentence from the control's label was my caution, not a
   requirement: the model is handed the exact list of controls that exist, so
   it has the facts to write something more useful than "Click X". What it
   must not do is describe a control it was not given, or claim an outcome it
   cannot see - the gateway prompt forbids both and this sanitises what comes
   back.

   Verification is the other half, and it is NOT a model problem. Whether a
   row was written is a fact on the server; no model knows it without reading
   it back, and "saved!" when nothing saved is the one failure that would
   discredit the product. So rather than restricting verification to authored
   guides, every step is checked against what observably changed on the page,
   and the walkthrough reports exactly that - including "I cannot see any
   change", which is a real answer and not a failure to have one.
--------------------------------------------------------------------------- */

const MAX_INSTRUCTION = 220;

/** Clean a model-written sentence, or return null if it is unusable. */
function cleanInstruction(raw) {
  /* Control characters are stripped by code point rather than by a regex
     range: a literal control-character class trips no-control-regex, and
     silencing the rule to keep the regex would be the wrong trade when the
     check reads more plainly this way. DEL (127) goes too. */
  const stripped = Array.from(String(raw || ''))
    .filter((ch) => { const c = ch.codePointAt(0); return c > 31 && c !== 127; })
    .join('');
  let s = stripped.replace(/\s+/g, ' ').trim();
  s = s.replace(/^[-*\d.)\s]+/, '');            // stray bullet or numbering
  if (!s || s.length < 3) return null;
  if (s.includes('<') || s.includes('>')) return null;   // never markup
  if (s.length > MAX_INSTRUCTION) s = s.slice(0, MAX_INSTRUCTION - 1).trimEnd() + '…';
  return s;
}

/**
 * Parse "id | instruction" lines. The id must be a real anchor; the sentence
 * is optional and falls back to the template, so a model that returns bare
 * ids still produces a usable walkthrough.
 */
export function parseSteps(answer, anchors) {
  const by = new Map(anchors.map((a) => [a.testid, a]));
  const out = [];
  const used = new Set();
  for (const line of String(answer || '').split(/[\r\n]+/)) {
    const t = line.trim();
    if (!t || t.toLowerCase() === 'none') continue;
    const bar = t.indexOf('|');
    const id = (bar === -1 ? t : t.slice(0, bar)).trim();
    const a = by.get(id);
    if (!a || used.has(id)) continue;            // invented, stale or repeated
    used.add(id);
    const said = bar === -1 ? null : cleanInstruction(t.slice(bar + 1));
    out.push({ ...a, say: said || instructionFor(a), authored: !!said });
  }
  return out;
}

/* What the page looks like right now, in the few dimensions a change shows up
   in. Deliberately cheap: this runs between every step. */
export function pageFingerprint() {
  const ids = new Set();
  try { for (const el of document.querySelectorAll('[data-testid]')) ids.add(el.getAttribute('data-testid')); } catch { /* ignore */ }
  let rows = 0;
  try { rows = document.querySelectorAll('tr').length; } catch { /* ignore */ }
  let text = '';
  try { text = (document.body.innerText || '').slice(0, 20000); } catch { /* ignore */ }
  return { ids, rows, len: text.length, alerts: alertTexts() };
}

/* Anything the application is currently SAYING. role=alert and role=status are
   where a save result lands when the app is built properly, so they are read
   first and quoted verbatim - the product's own words, never ours. */
function alertTexts() {
  const out = [];
  try {
    for (const el of document.querySelectorAll('[role="alert"],[role="status"],[aria-live="assertive"],[aria-live="polite"]')) {
      if (el.closest('#g360-wt-card')) continue;              // our own live region
      const r = el.getBoundingClientRect();
      if (r.width < 4 || r.height < 4) continue;
      const t = (el.textContent || '').replace(/\s+/g, ' ').trim();
      if (t && t.length <= 300) out.push(t);
    }
  } catch { /* ignore */ }
  return out;
}

const LOOKS_WRONG = /\b(error|failed|failure|could not|couldn't|cannot|unable|invalid|required|denied|rejected|try again)\b/i;

/**
 * What changed between two fingerprints, in the product's own words where it
 * said something, and otherwise as a plain observation.
 * ok === false  something went wrong and the page said so
 * ok === true   something observably changed
 * ok === null   nothing visible changed - stated, never dressed up as success
 */
export function describeChange(before, after) {
  const fresh = after.alerts.filter((a) => !before.alerts.includes(a));
  const bad = fresh.find((a) => LOOKS_WRONG.test(a));
  if (bad) return { ok: false, text: 'The page reported: "' + bad + '"' };
  if (fresh.length) return { ok: true, text: 'The page reported: "' + fresh[0] + '"' };

  const added = [...after.ids].filter((i) => !before.ids.has(i));
  const removed = [...before.ids].filter((i) => !after.ids.has(i));
  const dRows = after.rows - before.rows;

  if (dRows > 0) return { ok: true, text: dRows + (dRows === 1 ? ' new row' : ' new rows') + ' appeared.' };
  if (dRows < 0) return { ok: true, text: Math.abs(dRows) + (dRows === -1 ? ' row' : ' rows') + ' disappeared.' };
  if (added.length) return { ok: true, text: added.length + ' new control' + (added.length === 1 ? '' : 's') + ' appeared on the page.' };
  if (removed.length) return { ok: true, text: 'Part of the page closed or was replaced.' };
  if (Math.abs(after.len - before.len) > 40) return { ok: true, text: 'The page content changed.' };
  return { ok: null, text: 'I cannot see anything on the page that changed.' };
}
